import { defineStore } from 'pinia'
import { apiService } from '@/services/api'
import { formatDate, formatDateTime } from '@/components/logics/dateUtil'

export const useContractStore = defineStore('contract', {
  state: () => ({
    contract: null,
    formData: null,
    isReadOnly: false,
    // 価格情報（税込）
    pricing: {
      monthly: 30000,
      yearly: 300000,
    },
    formInit: {
      id: null,
      name: '',
      nameKana: '',
      corporateNumber: '',
      phone: ['', '', ''],
      fax: ['', '', ''],
      postalCode: ['', ''],
      prefecture: '',
      prefectureKana: '',
      address1: '',
      address1Kana: '',
      address2: '',
      address2Kana: '',
      contractStatus: 'trial',
      nextContractStatus: null,
      trialEndDate: null,
      paidPlanStartDate: null, // 有料プラン申込日
      currentPeriodStartDate: null, // 現在の契約期間開始日
      currentPeriodEndDate: null, // 現在の契約期間終了日
      trialEndAt: '',
      // subscriptionStatus: '', // PAYJP関連をコメントアウト
      // isCardExpiring: false, // PAYJP関連をコメントアウト
    },
  }),

  actions: {
    // 契約が有効かどうかをAPIで取得
    async getIsActive() {
      try {
        const response = await apiService.get('api/contracts/is-active')
        // レスポンスは { is_active: true/false }
        return response.isActive
      } catch (error) {
        return false
      }
    },
    // 現在の契約情報を取得してフォームデータも設定
    async getCurrentContract() {
      try {
        // Laravel側でログインユーザーから契約を取得
        const response = await apiService.get('api/contracts/current')
        this.contract = response.contract || response

        // 契約情報を取得したらフォームデータも更新
        this.formData = JSON.parse(JSON.stringify(this.contract))

        return true
      } catch (error) {
        // エラー時は初期値をセット
        this.clearFormData()
        return false
      }
    },

    // フォームデータの初期化（編集用）- getCurrentContractを使用することを推奨
    async setFormData() {
      return await this.getCurrentContract()
    },

    // フォームデータをクリア
    clearFormData() {
      this.formData = JSON.parse(JSON.stringify(this.formInit))
    },

    // 契約情報を更新
    async updateContract() {
      try {
        if (!this.formData || !this.formData.id) {
          throw new Error('更新する契約情報がありません')
        }

        const response = await apiService.put(`api/contracts/${this.formData.id}`, this.formData)

        // 更新後に最新の契約情報を再取得
        await this.getCurrentContract()

        return response.message || '契約情報を更新しました'
      } catch (error) {
        return null
      }
    },

    // 有料プランの値段を取得
    async getPlanPrices() {
      try {
        const response = await apiService.get('api/contracts/plan-prices')
        // レスポンスは { monthly: 30000, yearly: 300000 }
        this.pricing.monthly = response.monthly
        this.pricing.yearly = response.yearly
        return true
      } catch (error) {
        return null
      }
    },

    // 有料プラン解約処理
    async cancelPlan() {
      try {
        if (!this.formData || !this.formData.id) {
          throw new Error('解約する契約情報がありません')
        }

        const response = await apiService.post(`api/contracts/cancel`)

        // 解約後に最新の契約情報を再取得
        await this.getCurrentContract()

        return response.message || '有料プランの解約が完了しました'
      } catch (error) {
        return null
      }
    },

    // 有料プラン開始処理
    async startPaidContract(planType = 'monthly') {
      try {
        const response = await apiService.post('api/contracts/start-paid', {
          plan_type: planType,
        })

        // 有料プラン開始後に最新の契約情報を再取得
        await this.getCurrentContract()

        return response.message || '有料プランを開始しました'
      } catch (error) {
        return null
      }
    },

    // 月次プラン開始処理
    async startMonthlyPlan() {
      try {
        // next_contract_statusを設定してから有料プラン開始
        this.formData.nextContractStatus = 'paid_monthly'
        const response = await apiService.post('api/contracts/start-paid', {
          next_contract_status: 'paid_monthly',
        })

        // 有料プラン開始後に最新の契約情報を再取得
        await this.getCurrentContract()

        return response.message || '月次プランを開始しました'
      } catch (error) {
        return null
      }
    },

    // 年次プラン開始処理
    async startYearlyPlan() {
      try {
        // next_contract_statusを設定してから有料プラン開始
        this.formData.nextContractStatus = 'paid_yearly'
        const response = await apiService.post('api/contracts/start-paid', {
          next_contract_status: 'paid_yearly',
        })

        // 有料プラン開始後に最新の契約情報を再取得
        await this.getCurrentContract()

        return response.message || '年次プランを開始しました'
      } catch (error) {
        return null
      }
    },

    // プラン変更処理（月次⇔年次）
    async changePlan(newPlanType) {
      try {
        // APIで期待される形式に変換（'monthly' または 'yearly'）
        let planType
        if (newPlanType === 'paid_monthly' || newPlanType === 'monthly') {
          planType = 'monthly'
        } else if (newPlanType === 'paid_yearly' || newPlanType === 'yearly') {
          planType = 'yearly'
        } else {
          throw new Error('無効なプランタイプです')
        }

        const response = await apiService.post('api/contracts/change-plan', {
          plan_type: planType,
        })

        // プラン変更後に最新の契約情報を再取得
        await this.getCurrentContract()

        return response.message || 'プランの変更を受け付けました'
      } catch (error) {
        return null
      }
    },

    // 契約変更をキャンセルする処理
    async cancelContractChange() {
      try {
        if (!this.formData || !this.formData.id) {
          throw new Error('キャンセルする契約情報がありません')
        }

        const response = await apiService.post('api/contracts/cancel-change')

        // キャンセル後に最新の契約情報を再取得
        await this.getCurrentContract()

        return response.message || '契約変更をキャンセルしました'
      } catch (error) {
        return null
      }
    },

    // 郵便番号が入力されているかチェック
    isPostalCodeFilled() {
      // 0番目が3桁、1番目が4桁の配列であることを確認
      return this.formData.postalCode[0].length === 3 && this.formData.postalCode[1].length === 4
    },

    // 都道府県選択時に都道府県カナを設定
    setPrefectureKana(regionStore) {
      if (this.formData.prefecture && regionStore.prefectureKanaMap) {
        this.formData.prefectureKana = regionStore.prefectureKanaMap[this.formData.prefecture] || ''
      }
    },

    // 住所をセット
    setAddress(address) {
      if (!address) {
        return
      }

      this.formData.prefecture = address.prefecture

      // 都道府県カナをセット
      this.formData.prefectureKana = address.prefectureKana || ''

      this.formData.address1 = address.address1

      // 住所カナをセット（半角数字を全角数字に変換）
      if (address.address1Kana) {
        this.formData.address1Kana = address.address1Kana.replace(/[0-9]/g, function (s) {
          return String.fromCharCode(s.charCodeAt(0) + 0xfee0)
        })
      } else {
        this.formData.address1Kana = address.address1Kana || ''
      }
    },
  },

  getters: {
    // 価格の文字列形式取得ゲッター
    monthlyPriceFormatted() {
      return `¥${this.pricing.monthly.toLocaleString()}`
    },

    yearlyPriceFormatted() {
      return `¥${this.pricing.yearly.toLocaleString()}`
    },

    // プランタイプに応じた価格を取得
    getPriceFormatted() {
      return planType => {
        if (planType === 'monthly') {
          return this.monthlyPriceFormatted
        } else if (planType === 'yearly') {
          return this.yearlyPriceFormatted
        }
        return ''
      }
    },

    // 郵便番号をハイフン付きで取得
    postalCodeWithHyphen() {
      if (!this.formData || !this.formData.postalCode) return ''
      return this.formData.postalCode[0] + '-' + this.formData.postalCode[1]
    },

    // 契約ステータスマップ
    contractStatusMap() {
      return {
        trial: 'トライアル',
        paid_monthly: '有料プラン（月間プラン）',
        paid_yearly: '有料プラン（年間プラン）',
        expired: 'トライアル期限切れ',
        canceled: '解約',
      }
    },

    // 契約ステータスの日本語表示
    contractStatusDisplay() {
      if (!this.formData || !this.formData.contractStatus) return ''
      return this.contractStatusMap[this.formData.contractStatus] || this.formData.contractStatus
    },

    // 次契約ステータスの日本語表示
    nextContractStatusDisplay() {
      if (!this.formData || !this.formData.nextContractStatus) return ''
      return this.contractStatusMap[this.formData.nextContractStatus] || this.formData.nextContractStatus
    },

    // トライアル終了日時のフォーマット済み表示
    trialEndDateFormatted() {
      if (!this.formData || !this.formData.trialEndDate) return ''
      return formatDate(this.formData.trialEndDate, '/')
    },

    // 有料プラン申込日のフォーマット済み表示
    paidPlanStartDateFormatted() {
      if (!this.formData || !this.formData.paidPlanStartDate) return ''
      return formatDate(this.formData.paidPlanStartDate, '/')
    },

    // 解約日のフォーマット済み表示
    canceledAtFormatted() {
      if (!this.formData || !this.formData.canceledAt) return ''
      return formatDate(this.formData.canceledAt, '/')
    },

    // 契約期間開始日のフォーマット済み表示
    currentPeriodStartDateFormatted() {
      if (!this.formData || !this.formData.currentPeriodStartDate) return ''
      return formatDate(this.formData.currentPeriodStartDate, '/')
    },

    // 契約期間終了日のフォーマット済み表示
    currentPeriodEndDateFormatted() {
      if (!this.formData || !this.formData.currentPeriodEndDate) return ''
      return formatDate(this.formData.currentPeriodEndDate, '/')
    },

    // 契約ステータスの判定用getter
    isTrialStatus() {
      return this.formData?.contractStatus === 'trial'
    },

    isPaidStatus() {
      return ['paid_monthly', 'paid_yearly'].includes(this.formData?.contractStatus)
    },

    isPaidMonthlyStatus() {
      return this.formData?.contractStatus === 'paid_monthly'
    },

    isPaidYearlyStatus() {
      return this.formData?.contractStatus === 'paid_yearly'
    },

    isCanceledStatus() {
      return this.formData?.contractStatus === 'canceled'
    },

    isExpiredStatus() {
      return this.formData?.contractStatus === 'expired'
    },

    // トライアル残り日数を計算
    trialDaysRemaining() {
      if (!this.isTrialStatus || !this.formData?.trialEndDate) return null

      // 日付のみで比較するため、時刻を0時にリセット
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const trialEndDate = new Date(this.formData.trialEndDate)
      trialEndDate.setHours(0, 0, 0, 0)

      const diffTime = trialEndDate - today
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      return diffDays > 0 ? diffDays : 0
    },

    // 有料プラン契約ボタンを表示するかどうか
    shouldShowUpgradeButton() {
      return !this.isPaidStatus
    },

    // 次の契約プランが現在のプランと異なる場合のみ表示するかどうか
    shouldShowNextContractPlan() {
      if (!this.formData?.nextContractStatus) return false
      return this.formData.contractStatus !== this.formData.nextContractStatus
    },

    // 契約変更がスケジュールされているかどうか
    hasScheduledChange() {
      return this.formData?.nextContractStatus != null
    },

    // プラン変更・解約ボタンを表示するかどうか（変更がスケジュールされていない場合のみ表示）
    shouldShowPlanActions() {
      return this.isPaidStatus && !this.hasScheduledChange
    },

    // 変更キャンセルボタンを表示するかどうか
    shouldShowCancelChangeButton() {
      return this.hasScheduledChange
    },

    // 現在の契約情報を取得（formDataまたはcontractから）
    currentContract() {
      return this.formData || this.contract
    },

    // プランタイプの判定用ゲッター
    isCurrentPlanMonthly() {
      const contract = this.currentContract
      return contract?.contractStatus === 'paid_monthly'
    },

    isCurrentPlanYearly() {
      const contract = this.currentContract
      return contract?.contractStatus === 'paid_yearly'
    },

    // プランタイプを'monthly'/'yearly'形式で取得
    currentPlanType() {
      const contract = this.currentContract
      if (contract?.contractStatus === 'paid_monthly') return 'monthly'
      if (contract?.contractStatus === 'paid_yearly') return 'yearly'
      return null
    },

    // 指定したプランタイプが現在のプランかどうかを判定
    isCurrentPlan() {
      return planType => {
        const currentType = this.currentPlanType
        return currentType === planType
      }
    },
  },
})
